import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  computed,
  inject,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { TextareaModule } from 'primeng/textarea';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { getSubmissionsTypeOptions } from '../../spu.config';
import { GalleriaImage } from '@shared/components/galleria/galleria.component';

export interface PropertyValue {
  id: number;
  value: string;
  sort: number;
  asset?: string; // Asset identifier for single image
  imageUrl?: string; // Preview URL for single image
  isSaved?: boolean; // Add this to track save state
  isEditing?: boolean; // Add this to track editing state
  originalValue?: string; // Store original value for cancel functionality
  wasOriginallySaved?: boolean; // Track if this was originally a saved row (not a new row)
}

export interface PropertyFormData {
  id?: number;
  name: string;
  code: string;
  type: string;
  submissionsType: string;
  generateSku: boolean;
  remark: string;
  values: PropertyValue[];
}

@Component({
  selector: 'app-add-property-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    ButtonModule,
    DividerModule,
    TextareaModule,
    DataTableComponent,
    AppButtonComponent,
  ],
  templateUrl: './add-property-dialog.component.html',
  styleUrls: ['./add-property-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPropertyDialogComponent implements OnChanges {
  private _visible = false;
  private readonly translateService = inject(TranslateService);

  @Input()
  set visible(value: boolean) {
    this._visible = value;
  }

  get visible(): boolean {
    return this._visible;
  }
  @Input() initialData: PropertyFormData | null = null;
  @Input() viewMode = false;
  @Input() hideGenerateSku = false; // New input to hide Generate SKU field for package SPUs
  private _detailImages: GalleriaImage[] = [];

  @Input()
  set detailImages(value: GalleriaImage[]) {
    // Check if the value actually changed
    const isEqual =
      JSON.stringify(value) === JSON.stringify(this._detailImages);

    // Only update if the value actually changed
    if (isEqual) {
      return;
    }

    this._detailImages = value;
    // Update asset options when detailImages change
    this.updateAssetOptions();
  }

  get detailImages(): GalleriaImage[] {
    return this._detailImages;
  }
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<PropertyFormData>();

  propertyForm!: FormGroup;
  propertyValues = signal<PropertyValue[]>([]);
  selectedValues = signal<PropertyValue[]>([]);
  showValueTable = signal(false);
  showTextValueInput = signal(false);

  // Memoized asset options to prevent re-rendering
  private readonly _assetOptions = signal<any[]>([]);
  private _lastDetailImagesHash = '';

  // Computed property to check if any values are unsaved
  hasUnsavedValues = computed(() => {
    return this.propertyValues().some((v) => !v.isSaved);
  });

  displayTypeOptions: any[] = [];

  valueTableColumns: TableColumn[] = [
    {
      label: this.translateService.instant('goods.spu.attributes.fields.property_values'),
      prop: 'value',
      type: 'custom',
      width: 160,
    },
    {
      label: this.translateService.instant('goods.spu.attributes.fields.property_asset'),
      prop: 'asset',
      type: 'custom',
      width: 250,
    },
    { label: this.translateService.instant('goods.spu.table.columns.actions'), prop: 'actions', type: 'actions', width: 120 },
  ];

  valueTableColumnsView: TableColumn[] = [
    {
      label: this.translateService.instant('goods.spu.attributes.fields.property_values'),
      prop: 'value',
      type: 'custom',
      width: 160,
    },
    {
      label: this.translateService.instant('goods.spu.attributes.fields.property_asset'),
      prop: 'asset',
      type: 'custom',
      width: 250,
    },
  ];

  constructor(private readonly fb: FormBuilder) {
    // Initialize display type options with proper translation service
    this.displayTypeOptions = getSubmissionsTypeOptions(this.translateService);
    this.initializeForm();
  }

  private initializeForm(): void {
    // Initialize asset options
    this.updateAssetOptions();

    this.propertyForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      code: ['', Validators.maxLength(255)],
      submissionsType: ['', Validators.required],
      type: [0], // Default to Product Attribute
      generateSku: [false],
      remark: ['', Validators.maxLength(255)],
    });

    // Watch for display type changes
    this.propertyForm
      .get('submissionsType')
      ?.valueChanges.subscribe((value) => {
        const shouldShowTable = this.isOptionBasedSubmissionType(value);
        this.showValueTable.set(shouldShowTable);
        this.showTextValueInput.set(this.isTextBoxSubmissionType(value));
        if (!this.hideGenerateSku) {
          if (shouldShowTable) {
            this.propertyForm.get('generateSku')?.enable();
          } else {
            this.propertyForm.patchValue({ generateSku: false });
            this.propertyForm.get('generateSku')?.disable();
          }
        }
      });

    // Handle view mode after form initialization
    this.handleViewModeChange();
  }

  // Watch for initialData changes to populate form when editing
  ngOnChanges(changes: any): void {
    // Check if detailImages actually changed (not just reference)
    if (changes.detailImages) {
      const currentValue = changes.detailImages.currentValue;
      const previousValue = changes.detailImages.previousValue;
      const isDetailImagesEqual =
        JSON.stringify(currentValue) === JSON.stringify(previousValue);

      if (isDetailImagesEqual) {
        return; // Skip processing if detailImages hasn't actually changed
      }
    }

    if (this.initialData && this.propertyForm) {
      this.populateForm(this.initialData);
      this.handleViewModeChange();
    }
  }

  // Handle view mode changes
  private handleViewModeChange(): void {
    if (this.propertyForm) {
      if (this.viewMode) {
        this.propertyForm.disable();
      } else {
        this.propertyForm.enable();
        this.onDisplayTypeChange();
      }
    }
  }

  private populateForm(data: PropertyFormData): void {
    this.propertyForm.patchValue({
      name: data.name,
      code: data.code,
      submissionsType: data.submissionsType,
      type: data.type,
      generateSku: this.hideGenerateSku
        ? false
        : data.generateSku === true || String(data.generateSku) === 'Yes',
      remark: data.remark,
    });

    // Set property values
    if (data.values && data.values.length > 0) {
      this.propertyValues.set(
        data.values.map((v) => ({
          ...v,
          isSaved: true,
          isEditing: false,
          originalValue: v.value,
          wasOriginallySaved: true, // These are existing saved rows
        }))
      );
    } else {
      this.propertyValues.set([]);
    }

    // Update display type dependent UI
    this.onDisplayTypeChange();
  }

  onValueSelectionChange(selection: PropertyValue[]): void {
    this.selectedValues.set(selection);
  }
  
  onDisplayTypeChange(): void {
    const type = this.propertyForm.get('submissionsType')?.value;
    const shouldShowTable = this.isOptionBasedSubmissionType(type);
    const shouldShowTextInput = this.isTextBoxSubmissionType(type);
    this.showValueTable.set(shouldShowTable);
    this.showTextValueInput.set(shouldShowTextInput);

    if (!this.hideGenerateSku) {
      if (shouldShowTable) {
        this.propertyForm.get('generateSku')?.enable();
      } else {
        this.propertyForm.patchValue({ generateSku: false });
        this.propertyForm.get('generateSku')?.disable();
      }
    }

    // Add default blank item when display type requires values
    if (shouldShowTable && this.propertyValues().length === 0) {
      this.addDefaultBlankItem();
    }
    if (shouldShowTextInput && this.propertyValues().length === 0) {
      this.addDefaultBlankItem();
    }
  }

  private isOptionBasedSubmissionType(type: string): boolean {
    return [
      this.translateService.instant('common.form_types.radio_button'),
      this.translateService.instant('common.form_types.checkbox'),
      this.translateService.instant('common.form_types.dropdown'),
    ].includes(type);
  }

  private isTextBoxSubmissionType(type: string): boolean {
    return type === this.translateService.instant('common.form_types.text_box');
  }

  addDefaultBlankItem(): void {
    const newValue: PropertyValue = {
      id: Date.now(),
      value: '', // Ensure it starts empty
      sort: this.propertyValues().length + 1,
      asset: '', // Initialize asset as empty
      imageUrl: '', // Initialize imageUrl as empty
      isSaved: false, // Mark as not saved initially
      isEditing: true, // Start in editing mode
      wasOriginallySaved: false, // This is a new row
    };

    this.propertyValues.set([newValue]);
  }

  onAddValue(): void {
    const newValue: PropertyValue = {
      id: Date.now(),
      value: '', // Ensure it starts empty
      sort: this.propertyValues().length + 1,
      asset: '', // Initialize asset as empty
      imageUrl: '', // Initialize imageUrl as empty
      isSaved: false, // New values start as unsaved
      isEditing: true, // New values start in editing mode
      originalValue: '', // Store original empty value
      wasOriginallySaved: false, // This is a new row, never saved
    };

    this.propertyValues.set([...this.propertyValues(), newValue]);
  }

  onDeleteValue(id: number): void {
    const currentValues = this.propertyValues();
    const filteredValues = currentValues.filter((v) => v.id !== id);
    this.propertyValues.set(filteredValues);
  }

  onDeleteSelectedValues(): void {
    const currentValues = this.propertyValues();
    const selectedIds = new Set(this.selectedValues().map(v => v.id));
    const filteredValues = currentValues.filter((v) => !selectedIds.has(v.id));
    this.propertyValues.set(filteredValues);
    this.selectedValues.set([]); // Clear selection after deletion
  }

  onInputChange(value: PropertyValue, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;

    // Update the value directly without triggering signal updates
    value.value = newValue;
    value.isSaved = false;
    value.isEditing = true;

    // Don't update the signal immediately to prevent focus loss
    // The signal will be updated when Save/Cancel buttons are clicked
  }

  private updatePropertyValue(id: number, newValue: string): void {
    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) =>
      v.id === id
        ? { ...v, value: newValue, isSaved: false, isEditing: true }
        : v
    );
    this.propertyValues.set(updatedValues);
  }

  onValueModelChange(value: PropertyValue): void {
    // This method is called when ngModel changes
    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) =>
      v.id === value.id ? { ...v, isSaved: false, isEditing: true } : v
    );
    this.propertyValues.set(updatedValues);
  }

  getTextBoxValue(): string {
    return this.propertyValues()[0]?.value || '';
  }

  onTextBoxValueChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const currentValues = this.propertyValues();
    const existingValue = currentValues[0];
    const textValue: PropertyValue = {
      id: existingValue?.id || Date.now(),
      value: target.value,
      sort: existingValue?.sort || 1,
      asset: '',
      imageUrl: '',
      isSaved: false,
      isEditing: true,
      originalValue: existingValue?.originalValue || '',
      wasOriginallySaved: existingValue?.wasOriginallySaved || false,
    };

    this.propertyValues.set([textValue]);
  }

  // Get asset dropdown options from detail images (memoized)
  getAssetOptions(): any[] {
    return this._assetOptions();
  }

  // Update asset options when detailImages change
  private updateAssetOptions(): void {
    // Create a hash to check if detailImages actually changed
    const currentHash = JSON.stringify(
      this.detailImages?.map((img) => ({
        id: img.id,
        src: img.src,
        title: img.title,
      }))
    );

    if (currentHash === this._lastDetailImagesHash) {
      return; // Skip update if data hasn't changed
    }

    this._lastDetailImagesHash = currentHash;

    if (!this.detailImages || this.detailImages.length === 0) {
      this._assetOptions.set([
        { label: this.translateService.instant('goods.spu.attributes.no_images_found'), value: '', disabled: true },
      ]);
      return;
    }

    const options = this.detailImages.map((image, index) => ({
      label:
        image.title ||
        this.extractFileNameFromUrl(image.src) ||
        `image_${index + 1}`,
      value: image.src || image.id || `image_${index + 1}`,
      image: image.src || image.thumbnailSrc,
      originalName:
        image.title ||
        this.extractFileNameFromUrl(image.src) ||
        `image_${index + 1}`,
    }));

    this._assetOptions.set(options);
  }

  // Truncate file name if too long
  private getTruncatedFileName(fileName: string): string {
    if (!fileName) return 'Unknown';
    if (fileName.length <= 20) return fileName;
    return fileName.substring(0, 17) + '...';
  }

  // Extract file name from URL
  private extractFileNameFromUrl(url: string): string {
    if (!url) return 'Unknown';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop() || 'Unknown';
      return fileName;
    } catch {
      // If URL parsing fails, try to extract from the string
      const parts = url.split('/');
      return parts.at(-1) || 'Unknown';
    }
  }

  // Handle asset selection change
  onAssetChange(value: PropertyValue, selectedAsset: string): void {
    // Find the selected asset option to get the imageUrl
    const selectedAssetOption = this._assetOptions().find(
      (opt) => opt.value === selectedAsset
    );
    const imageUrl = selectedAssetOption?.image || '';

    // If no asset is selected, clear the imageUrl
    const finalImageUrl = selectedAsset ? imageUrl : '';

    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) =>
      v.id === value.id
        ? {
            ...v,
            asset: selectedAsset,
            imageUrl: finalImageUrl,
            isSaved: false,
            isEditing: true,
          }
        : v
    );
    this.propertyValues.set(updatedValues);
  }

  // Get selected asset info for display
  getSelectedAssetInfo(assetValue: string): any {
    if (!assetValue) return null;
    return this._assetOptions().find((option) => option.value === assetValue);
  }

  // Debug methods for template
  onDropdownShow(): void {
    // Fix positioning after dropdown opens
    setTimeout(() => {
      this.fixDropdownPosition();
    }, 0);
  }

  // Fix dropdown positioning
  private fixDropdownPosition(): void {
    const dropdown = document.querySelector(
      '.asset-dropdown-panel'
    ) as HTMLElement;
    if (dropdown) {
      // Find the active p-select element in the table - use more flexible selector
      const activePSelect = document.querySelector(
        '.p-select.p-select-open, .p-select.p-focus, .p-select:focus, .asset-select.p-select-open'
      ) as HTMLElement;

      if (activePSelect) {
        const pSelectRect = activePSelect.getBoundingClientRect();

        // Match the width of the p-select input
        dropdown.style.width = `${pSelectRect.width}px`;
        dropdown.style.maxWidth = `${pSelectRect.width}px`;
        dropdown.style.minWidth = `${pSelectRect.width}px`;

        // Position it directly below the p-select with proper viewport consideration
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 200; // max-height from CSS
        const spaceBelow = viewportHeight - pSelectRect.bottom;

        if (spaceBelow >= dropdownHeight) {
          // Position below the input
          dropdown.style.left = `${pSelectRect.left}px`;
          dropdown.style.top = `${pSelectRect.bottom}px`;
        } else {
          // Position above the input if not enough space below
          dropdown.style.left = `${pSelectRect.left}px`;
          dropdown.style.top = `${pSelectRect.top - dropdownHeight}px`;
        }

        dropdown.style.right = 'auto';
        dropdown.style.bottom = 'auto';
        dropdown.style.transform = 'none';
      } else {
        // Fallback to auto width
        dropdown.style.width = 'auto';
        dropdown.style.maxWidth = '300px';
        dropdown.style.minWidth = '200px';
      }
    }
  }

  onDropdownHide(): void {}

  // Handle image loading errors
  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
    }
  }

  // New methods for save/edit functionality
  onSaveValue(id: number): void {
    // First update the signal with the current input value
    const currentValues = this.propertyValues();
    const valueToSave = currentValues.find((v) => v.id === id);
    if (valueToSave) {
      this.updatePropertyValue(id, valueToSave.value);
    }

    // Then mark only the specific value as saved and not editing
    const updatedValues = currentValues.map((v) =>
      v.id === id
        ? { ...v, isSaved: true, isEditing: false, wasOriginallySaved: true }
        : v
    );
    this.propertyValues.set(updatedValues);
  }

  onEditValue(id: number): void {
    // Mark only the specific value as editing and store original value
    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) =>
      v.id === id ? { ...v, isEditing: true, originalValue: v.value } : v
    );
    this.propertyValues.set(updatedValues);
  }

  onCancelEdit(id: number): void {
    // Mark only the specific value as not editing and restore original value
    const currentValues = this.propertyValues();
    const valueToCancel = currentValues.find((v) => v.id === id);

    if (valueToCancel) {
      // If this was a saved row, restore the original value and exit edit mode
      if (valueToCancel.wasOriginallySaved) {
        const updatedValues = currentValues.map((v) =>
          v.id === id
            ? { ...v, isEditing: false, value: v.originalValue || v.value }
            : v
        );
        this.propertyValues.set(updatedValues);
      } else {
        // Otherwise (it was a new row, never saved), remove it completely when cancel is clicked
        this.onDeleteValue(id);
      }
    }
  }

  onSaveAllValues(): void {
    // Mark all values as saved
    const currentValues = this.propertyValues();
    const updatedValues = currentValues.map((v) => ({
      ...v,
      isSaved: true,
      wasOriginallySaved: true,
    }));
    this.propertyValues.set(updatedValues);
  }

  maxLengthParams(labelKey: string, requiredLength: number): Record<string, unknown> {
    return { fieldName: this.translateService.instant(labelKey), requiredLength };
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.resetForm();
  }

  onConfirm(): void {
    if (this.propertyForm.valid) {
      const formData: PropertyFormData = {
        ...this.propertyForm.value,
        generateSku: this.hideGenerateSku
          ? false
          : this.propertyForm.value.generateSku,
        values: this.propertyValues(),
      };
      this.confirm.emit(formData);
      this.onCancel();
    }
  }

  private resetForm(): void {
    this.propertyForm.reset();
    this.propertyValues.set([]);
    this.selectedValues.set([]);
    this.showValueTable.set(false);
    this.showTextValueInput.set(false);

    // Reset form controls to default state
    this.propertyForm.patchValue({
      name: '',
      code: '',
      submissionsType: '', // Default to Radio Button
      type: 0, // Default to Product Attribute
      generateSku: false,
      remark: '',
    });

    // Re-enable generateSku control only if not hidden
    if (!this.hideGenerateSku) {
      this.propertyForm.get('generateSku')?.enable();
    }
  }
}
